import type {
  OpportunityStageModel,
  CreateOpportunityStageDto,
  UpdateOpportunityStageDto,
  OpportunityStageFilters,
} from '@/types/opportunity-stage.types';
import { ApiService } from './api.service';
import { endpoints } from './endpoints';

interface PaginatedStagesResponse {
  docs: OpportunityStageModel[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export class OpportunityStagesApiService extends ApiService {
  // Get all opportunity stages
  getAll = async (activeOnly = false): Promise<OpportunityStageModel[]> =>
    this.get(`${endpoints.opportunityStages.root}?activeOnly=${activeOnly}`);

  // Get paginated opportunity stages
  getPaginated = async (params: {
    page: number;
    limit: number;
    filters?: OpportunityStageFilters;
  }): Promise<PaginatedStagesResponse> => {
    const { page, limit, filters = {} } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ...(filters.search && { search: filters.search }),
      ...(filters.type && { type: filters.type }),
      ...(filters.active !== undefined && { active: String(filters.active) }),
    });

    return this.get(`${endpoints.opportunityStages.root}/paginated?${queryParams.toString()}`);
  };

  // Get opportunity stage by ID
  getById = async (id: string): Promise<OpportunityStageModel> =>
    this.get(endpoints.opportunityStages.detail(id));

  // Create new opportunity stage
  create = async (data: CreateOpportunityStageDto): Promise<OpportunityStageModel> =>
    this.post(endpoints.opportunityStages.root, data);

  // Update opportunity stage
  updateStage = async (id: string, data: UpdateOpportunityStageDto): Promise<OpportunityStageModel> =>
    this.put(endpoints.opportunityStages.detail(id), data);

  // Delete opportunity stage
  deleteStage = async (id: string): Promise<void> =>
    this.delete(endpoints.opportunityStages.detail(id));

  // Reorder opportunity stages
  reorder = async (stages: Array<{ id: string; order: number }>): Promise<void> =>
    this.post(`${endpoints.opportunityStages.root}/reorder`, stages);
}

// Export singleton instance
export const opportunityStagesService = new OpportunityStagesApiService();

// Export individual functions for backwards compatibility
export const getAllOpportunityStages = opportunityStagesService.getAll;
export const getOpportunityStagesPaginated = opportunityStagesService.getPaginated;
export const getOpportunityStageById = opportunityStagesService.getById;
export const createOpportunityStage = opportunityStagesService.create;
export const updateOpportunityStage = opportunityStagesService.updateStage;
export const deleteOpportunityStage = opportunityStagesService.deleteStage;
export const reorderOpportunityStages = opportunityStagesService.reorder;
